import { Box, ButtonBase, styled } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

import {
    COLORS,
    PAPER_MAX_WIDTH,
    RADIUS,
    SHADOW,
    TYPOGRAPHY,
} from "./CreationModal.constants";

/** Keeps `$`-prefixed styling props from reaching MUI components / the DOM. */
export const transientOptions = {
    shouldForwardProp: (prop: PropertyKey) => !String(prop).startsWith("$"),
};

/**
 * The global theme CSS forces `button.MuiButtonBase-root { min-width: 180px }`;
 * this out-specifies it for the compact buttons used here.
 */
export const buttonBaseReset = {
    "&.MuiButtonBase-root": {
        minWidth: 0,
        boxSizing: "border-box" as const,
    },
};

/** `GenericModal` `paperSx` for the creation modals (960px, 20px radius). */
export const creationPaperSx = {
    width: "100%",
    maxWidth: PAPER_MAX_WIDTH,
    borderRadius: RADIUS.paper,
    border: `1px solid rgba(226, 232, 240, 0.8)`,
    boxShadow: SHADOW.paper,
    color: COLORS.slate900,
    "& *": { fontFamily: "inherit" },
};

export const creationHeaderSx = {
    gap: "16px",
    px: 3,
    pt: 3,
    pb: 2,
    borderBottom: `1px solid ${COLORS.slate100}`,
    "& h2": {
        ...TYPOGRAPHY.title,
        color: COLORS.slate900,
    },
    "& h2 + p": {
        ...TYPOGRAPHY.bodySm,
        fontSize: "13px",
        color: COLORS.slate500,
    },
};

export const creationContentSx = {
    backgroundColor: COLORS.white,
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
        background: COLORS.slate200,
        borderRadius: RADIUS.full,
    },
    "&::-webkit-scrollbar-thumb:hover": { background: COLORS.slate300 },
};

/** Configure step: light slate canvas behind the white form cards. */
export const creationConfigureContentSx = {
    ...creationContentSx,
    backgroundColor: "rgba(248, 250, 252, 0.6)",
};

export const creationFooterSx = {
    px: 3,
    py: 2,
    borderTop: `1px solid rgba(226, 232, 240, 0.8)`,
    backgroundColor: "rgba(248, 250, 252, 0.7)",
};

export const CatalogContent = styled(Box)({
    padding: 24,
});

/** Padded column used by configure, mapping, and tracker steps. */
export const PaddedContent = styled(Box)({
    padding: 24,
    display: "flex",
    flexDirection: "column",
    gap: 24,
});

/** White card wrapping a form section on the configure step. */
export const FormCard = styled(Box)({
    backgroundColor: COLORS.white,
    padding: 24,
    borderRadius: RADIUS.lg,
    border: `1px solid rgba(226, 232, 240, 0.8)`,
    boxShadow: SHADOW.sm,
});

export const PillButton = styled(
    ButtonBase,
    transientOptions,
)<{ $active?: boolean }>(({ $active }) => ({
    ...buttonBaseReset,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    width: "fit-content",
    flex: "0 0 auto",
    padding: "6px 14px",
    borderRadius: RADIUS.full,
    whiteSpace: "nowrap",
    transition: "background-color 150ms, color 150ms",
    ...TYPOGRAPHY.labelSm,
    ...($active
        ? {
              backgroundColor: COLORS.slate900,
              color: COLORS.white,
              boxShadow: SHADOW.sm,
          }
        : {
              backgroundColor: COLORS.slate100,
              color: COLORS.slate600,
              "&:hover": { backgroundColor: "rgba(226, 232, 240, 0.7)" },
          }),
    "& .pill-count": {
        color: $active ? COLORS.slate300 : COLORS.slate400,
        fontSize: 11,
    },
}));

/**
 * Footer buttons are the app's `StandartButton`s (black outlined Back, primary
 * contained action), as in the SignalWizard footer. `StandartButton` styles
 * its text with `!important`, so the few tweaks here must do the same.
 */
export const creationButtonSx: SxProps<Theme> = {
    textTransform: "none !important",
    gap: "2px",
    "&.Mui-disabled": { opacity: 0.4 },
    "& .MuiButton-startIcon svg, & .MuiButton-endIcon svg": { fontSize: 16 },
};

export const creationCancelButtonSx: SxProps<Theme> = {
    ...creationButtonSx,
    color: `${COLORS.slate600} !important`,
    fontWeight: "500 !important",
    "&:hover": {
        color: `${COLORS.slate900} !important`,
        backgroundColor: "rgba(226, 232, 240, 0.5) !important",
    },
};

/** Brand logo shown before a footer submit label (e.g. OAuth "Connect with X"). */
export const FooterSubmitLogo = styled("img")({
    width: 16,
    height: 16,
    objectFit: "contain",
    display: "block",
});

export const StatusChip = styled(Box)(({ theme }) => ({
    display: "none",
    [theme.breakpoints.up("sm")]: {
        display: "inline-flex",
    },
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    maxWidth: "100%",
    borderRadius: RADIUS.sm,
    border: `1px solid rgba(226, 232, 240, 0.7)`,
    backgroundColor: COLORS.white,
    color: COLORS.slate600,
    boxShadow: SHADOW.xs,
    ...TYPOGRAPHY.labelSm,
    "& .status-dot": {
        width: 8,
        height: 8,
        borderRadius: RADIUS.full,
        backgroundColor: COLORS.primary,
        flexShrink: 0,
    },
    "& .status-text": {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
}));
